import React, {useEffect, useRef, useState, useCallback, useMemo} from 'react'
import {useFragmentDetection} from '../hooks/useFragmentDetection'
import {FRAGMENT_DETECTION} from '../../../../constant/fragmentDetection'
import useDebounce from '../../../../hooks/useDebounce'
import FragmentHighlight from '../../../step2/fragment-ui/FragmentHighlight'
import {showFragmentWarning} from '../../../step2/fragment-ui/FragmentWarning'
import SelectionTransformer from './SelectionTransformer'
import CanvasHost from './CanvasHost'
import TextEditingOverlay from './TextEditingOverlay'
import {VIEW_MODES} from '../../constants/editorConfig'

const EDITOR_CANVAS_FRAGMENT_OPTIONS = {
    minRegionSize: FRAGMENT_DETECTION.MIN_REGION_SIZE,
    minWarningSize: FRAGMENT_DETECTION.MIN_WARNING_SIZE,
    minThickness: FRAGMENT_DETECTION.MIN_THICKNESS,
    alphaThreshold: FRAGMENT_DETECTION.ALPHA_THRESHOLD,
    maxCanvasSize: FRAGMENT_DETECTION.MAX_CANVAS_SIZE,
    method: 'auto',
    dpi: null,
    enableDetection: true,
}

const EditorCanvas = ({
    runtime,
    state,
    width = 0,
    height = 0,
    viewMode = VIEW_MODES.OVERLAY,
    enableDetection = true,
    onFragmentsChange,
}) => {
    const hostRefs = useRef(null)

    const fragmentDetectionOptions = useMemo(
        () => ({...EDITOR_CANVAS_FRAGMENT_OPTIONS, enableDetection}),
        [enableDetection],
    )
    const {fragments, runDetection, isDetecting, clearFragments} = useFragmentDetection(fragmentDetectionOptions)
    const [showHighlights, setShowHighlights] = useState(true)

    const nodeIdsKey = useMemo(() => {
        if (state?.order?.length > 0) {
            return state.order.slice().sort().join(',')
        }
        if (state?.nodes?.size) {
            return Array.from(state.nodes.keys()).sort().join(',')
        }
        return ''
    }, [state?.order, state?.nodes])

    const contentKey = useMemo(() => {
        if (!nodeIdsKey || !state?.nodes?.size) return ''
        return nodeIdsKey
            .split(',')
            .map((id) => {
                const m = state.nodes.get?.(id)
                if (!m?.geometry) return id
                const g = m.geometry
                const style = m.style ?? {}
                const strokePart = `s:${style.stroke ?? ''},${style.strokeWidth ?? 0}`
                let part = `${id}:${g.x},${g.y},${g.width},${g.height},${g.rotation ?? 0},${strokePart}`
                if (m.type === 'text') {
                    part += ',' + (m.content?.text ?? style.text ?? '')
                }
                return part
            })
            .join('|')
    }, [nodeIdsKey, state?.nodes])

    const debouncedContentKey = useDebounce(contentKey, FRAGMENT_DETECTION.DETECTION_DEBOUNCE_MS)

    const selectionIsOnlySvgShape = useMemo(() => {
        const ids = state?.selection?.nodeIds
        if (!ids?.length || !state?.nodes) return false
        return ids.every((id) => state.nodes.get?.(id)?.type === 'svg-shape')
    }, [state?.selection?.nodeIds, state?.nodes])

    const runDetectionWithCanvas = useCallback(() => {
        const refs = hostRefs.current
        if (!refs?.layerRefSilhouette?.current) return
        const layer = refs.layerRefSilhouette.current
        const stage = refs.stageRef?.current
        const wasVisible = layer.visible()
        layer.visible(true)
        if (stage?.batchDraw) stage.batchDraw()
        const canvas = layer.toCanvas({
            pixelRatio: 1,
            mimeType: 'image/png',
        })
        layer.visible(wasVisible)
        if (stage?.batchDraw) stage.batchDraw()

        runDetection(canvas)
    }, [runDetection])

    useEffect(() => {
        if (debouncedContentKey.length > 0) {
            const timer = setTimeout(() => {
                runDetectionWithCanvas()
            }, 100)
            return () => clearTimeout(timer)
        } else {
            clearFragments()
        }
    }, [debouncedContentKey, clearFragments, runDetectionWithCanvas])

    const worldFragments = useMemo(() => {
        if (!fragments || fragments.length === 0) return fragments
        const stage = hostRefs.current?.stageRef?.current
        const stageScale = stage?.scale?.()
        const stagePosition = stage?.position?.()
        const scale = stageScale?.x ?? 1
        const x = scale !== 0 ? -(stagePosition?.x ?? 0) / scale : 0
        const y = scale !== 0 ? -(stagePosition?.y ?? 0) / scale : 0

        if (!scale || scale === 1) return fragments

        const transformed = fragments.map((f) => ({
            ...f,
            x: f.x / scale + x,
            y: f.y / scale + y,
            width: f.width / scale,
            height: f.height / scale,
        }))

        return transformed
    }, [fragments])

    useEffect(() => {
        if (typeof onFragmentsChange === 'function') {
            onFragmentsChange(fragments.length)
        }
        if (worldFragments && worldFragments.length > 0) {
            const thinFragments = worldFragments.filter((f) => f.hasThinRegion)
            if (thinFragments.length > 0) {
                showFragmentWarning(
                    worldFragments,
                    `Có ${thinFragments.length} vùng nét mảnh. Tăng Stroke trong panel layer để đủ điều kiện in.`,
                )
            } else {
                showFragmentWarning(worldFragments)
            }
        }
    }, [worldFragments, onFragmentsChange])

    const handleCanvasReady = useCallback((refs) => {
        hostRefs.current = refs
    }, [])

    return (
        <div style={{position: 'relative', width: '100%', height: '100%'}}>
            <CanvasHost
                runtime={runtime}
                width={width}
                height={height}
                viewMode={viewMode}
                onReady={handleCanvasReady}
                highlightLayer={
                    <FragmentHighlight fragments={worldFragments} visible={showHighlights && !isDetecting} />
                }
            >
                <SelectionTransformer
                    selection={state?.selection || {nodeIds: []}}
                    isOnlySvgShape={selectionIsOnlySvgShape}
                />
            </CanvasHost>

            <TextEditingOverlay runtime={runtime} />

            {isDetecting && (
                <div
                    style={{
                        position: 'absolute',
                        top: 40,
                        right: 10,
                        padding: '8px 12px',
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        color: 'white',
                        borderRadius: '4px',
                        fontSize: '12px',
                        zIndex: 1000,
                    }}
                >
                    Đang phân tích...
                </div>
            )}

            {!isDetecting && worldFragments && worldFragments.length > 0 && (
                <div
                    style={{
                        position: 'absolute',
                        top: 10,
                        left: 10,
                        padding: '8px 12px',
                        backgroundColor:
                            worldFragments.length > 10
                                ? FRAGMENT_DETECTION.HIGHLIGHT_COLORS.error
                                : worldFragments.length > 5
                                  ? FRAGMENT_DETECTION.HIGHLIGHT_COLORS.warning
                                  : FRAGMENT_DETECTION.HIGHLIGHT_COLORS.info,
                        color: 'white',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        zIndex: 1000,
                        cursor: 'pointer',
                    }}
                    onClick={() => setShowHighlights(!showHighlights)}
                    title="Click để ẩn/hiện highlights"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            setShowHighlights(!showHighlights)
                        }
                    }}
                >
                    {worldFragments.length} fragments
                </div>
            )}
        </div>
    )
}

export default EditorCanvas
